import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Button, 
  Typography, 
  Paper, 
  Grid,
  TextField,
  FormControl,
  FormControlLabel,
  FormGroup,
  Switch,
  Divider,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Snackbar
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import { getMarketingSettings, updateMarketingSettings } from '../../firebase/services/marketingSettingsService';
import ImageSelectorDialog from '../../components/email/ImageSelectorDialog';

/**
 * Component for managing marketing settings
 */
const MarketingSettings = () => {
  // State for settings fields (matching Firestore schema)
  const [defaultSenderName, setDefaultSenderName] = useState('');
  const [defaultSenderEmail, setDefaultSenderEmail] = useState('');
  const [complianceFooter, setComplianceFooter] = useState('');
  const [unsubscribeLinkText, setUnsubscribeLinkText] = useState('Unsubscribe');
  const [brandingLogoUrl, setBrandingLogoUrl] = useState('');
  const [sendingFrequency, setSendingFrequency] = useState('normal');

  // UI state
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [logoDialogOpen, setLogoDialogOpen] = useState(false);

  // Load settings from Firestore on mount
  useEffect(() => {
    setLoading(true);
    getMarketingSettings()
      .then(data => {
        if (data) {
          setDefaultSenderName(data.defaultSenderName || '');
          setDefaultSenderEmail(data.defaultSenderEmail || '');
          setComplianceFooter(data.complianceFooter || '');
          setUnsubscribeLinkText(data.unsubscribeLinkText || 'Unsubscribe');
          setBrandingLogoUrl(data.brandingLogoUrl || '');
        }
        setLoading(false);
      })
      .catch(() => {
        setError('Failed to load settings');
        setLoading(false);
      });
  }, []);

  // Handle settings save
  const handleSaveSettings = async () => {
    setError(null);
    try {
      await updateMarketingSettings({
        defaultSenderName,
        defaultSenderEmail,
        complianceFooter,
        unsubscribeLinkText,
        brandingLogoUrl,
      });
      setSnackbarOpen(true);
    } catch (err: any) {
      setError(err?.message || 'Failed to save settings');
    }
  };

  if (loading) return <Typography>Loading...</Typography>;

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1">
          Marketing Settings
        </Typography>
        <Button 
          variant="contained" 
          color="primary"
          startIcon={<SaveIcon />} 
          onClick={handleSaveSettings}
        >
          Save Settings
        </Button>
      </Box>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Email Sender Information
        </Typography>
        <Grid container spacing={3}>
          <Grid
            xs={12}>
            <TextField
              label="Default From Name"
              fullWidth
              value={defaultSenderName}
              onChange={(e) => setDefaultSenderName(e.target.value)}
              helperText="This name will appear as the sender in email clients"
            />
          </Grid>
          <Grid
            xs={12}>
            <TextField
              label="Sender Email Address"
              fullWidth
              type="email"
              value={defaultSenderEmail}
              onChange={(e) => setDefaultSenderEmail(e.target.value)}
              helperText="Must be verified in your email service provider"
            />
          </Grid>
        </Grid>
      </Paper>
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Email Template Settings
        </Typography>
        <Grid container spacing={3}>
          <Grid xs={12}>
            <TextField
              label="Default Email Footer Text"
              fullWidth
              multiline
              rows={2}
              value={complianceFooter}
              onChange={(e) => setComplianceFooter(e.target.value)}
            />
          </Grid>
          <Grid
            xs={12}>
            <Typography variant="body2" gutterBottom>Branding Logo</Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <Button 
                variant="outlined" 
                onClick={() => setLogoDialogOpen(true)}
                size="small"
              >
                Select Logo
              </Button>
              {brandingLogoUrl && (
                <Button 
                  variant="outlined" 
                  color="error" 
                  size="small"
                  onClick={() => setBrandingLogoUrl('')}
                >
                  Remove
                </Button>
              )}
            </Box>
            {brandingLogoUrl && (
              <Box sx={{ mb: 2, textAlign: 'center' }}>
                <img 
                  src={brandingLogoUrl} 
                  alt="Logo preview" 
                  style={{ maxWidth: '100%', maxHeight: '60px', marginBottom: '8px' }} 
                />
                <Typography variant="caption" display="block" color="text.secondary">
                  Logo Preview
                </Typography>
              </Box>
            )}
            <ImageSelectorDialog
              open={logoDialogOpen}
              onClose={() => setLogoDialogOpen(false)}
              onSelect={(imageUrl) => {
                setBrandingLogoUrl(imageUrl);
                setLogoDialogOpen(false);
              }}
              title="Select Logo Image"
              defaultCategory="LOGOS"
              singleCategory={true}
              allowUpload={true}
            />
          </Grid>
          <Grid
            xs={12}>
            <TextField
              label="Unsubscribe Link Text"
              fullWidth
              value={unsubscribeLinkText}
              onChange={(e) => setUnsubscribeLinkText(e.target.value)}
              helperText="Text for the unsubscribe link in emails"
            />
          </Grid>
        </Grid>
      </Paper>
      <Paper sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom>
          Sending Settings
        </Typography>
        <Grid container spacing={3}>
          <Grid
            xs={12}>
            <FormControl fullWidth>
              <InputLabel>Sending Frequency</InputLabel>
              <Select
                value={sendingFrequency}
                label="Sending Frequency"
                onChange={(e) => setSendingFrequency(e.target.value)}
              >
                <MenuItem value="conservative">Conservative (avoid sending too many emails)</MenuItem>
                <MenuItem value="normal">Normal (balanced frequency)</MenuItem>
                <MenuItem value="aggressive">Aggressive (maximize email touches)</MenuItem>
              </Select>
            </FormControl>
          </Grid>
        </Grid>
        <Alert severity="info" sx={{ mt: 3 }}>
          Email deliverability and engagement rates are affected by your sending practices. 
          We recommend maintaining a consistent sending schedule and respecting user preferences.
        </Alert>
      </Paper>
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={() => setSnackbarOpen(false)}
        message="Settings saved successfully"
      />
    </Box>
  );
};

export default MarketingSettings; 